import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { ANZS_GREETING_TAXONOMY_ID, NA_GREETING_TAXONOMY_ID, NA_HC_GALLERY_ID, ANZS_HC_GALLERY_ID } from '~/client/constants'
import { UseGreetingDescriptionResult } from '~/client/hooks/useGreetingDescription/interface'
import { useTranslations } from '~/client/hooks/useTranslations'
import { getGalleryIdSelector } from '~/client/store/config'
import { categoryByIdSelector } from '~/client/store/filterCategories'
import { quickViewFilterByIdSelector } from '~/client/store/quickViewFilter'
import { qvFilterNameByTaxonomyIdSelector } from '~/client/store/qvFilterNameByTaxonomyId'

export const useGreetingDescription = (tileEntity: State.TileEntity): UseGreetingDescriptionResult => {
  const galleryId = useSelector(getGalleryIdSelector)

  const categoryById = useSelector(categoryByIdSelector)
  const localize = useTranslations()

  const qvFilterNameByTaxonomyId = useSelector(qvFilterNameByTaxonomyIdSelector)
  const selectQuickViewFilterById = useSelector(quickViewFilterByIdSelector)

  // Greeting options will be the first taxonomy filter that has a corresponding QV filter name
  const [firstQvTaxonomyFilterId] = useMemo(
    () =>
      Object.keys(tileEntity.availableTaxonomyVariationsForExpansion || {}).filter(
        (key) => (key === NA_GREETING_TAXONOMY_ID || key === ANZS_GREETING_TAXONOMY_ID) && !!qvFilterNameByTaxonomyId[key],
      ),
    [tileEntity, qvFilterNameByTaxonomyId],
  )

  const calloutOptionsArr = useMemo(
    () => (firstQvTaxonomyFilterId && tileEntity.availableTaxonomyVariationsForExpansion[firstQvTaxonomyFilterId]) || [],
    [tileEntity, firstQvTaxonomyFilterId],
  )

  const greetingOptions = useMemo(() => calloutOptionsArr.map((option) => categoryById(option).title).join(', '), [calloutOptionsArr, categoryById])

  const shouldShowGreetingOptions = useMemo(() => calloutOptionsArr.length > 1, [calloutOptionsArr])
  const greetingTitle = useMemo(
    () => (firstQvTaxonomyFilterId ? selectQuickViewFilterById(qvFilterNameByTaxonomyId[firstQvTaxonomyFilterId])?.title : ''),
    [firstQvTaxonomyFilterId, selectQuickViewFilterById, qvFilterNameByTaxonomyId],
  )

  const formattedGreetingTitle = greetingTitle ? `${greetingTitle} ${localize('GreetingOptions')}: ` : ''

  // Only show greeting options for ANZS and NA HC gallery
  // Remove this once we have a proper way to handle the taxonomy variation options for all galleries
  if (galleryId !== NA_HC_GALLERY_ID && galleryId !== ANZS_HC_GALLERY_ID) {
    return {
      greetingTitle: '',
      greetingOptions: '',
      shouldShowGreetingOptions: false,
    }
  }

  return {
    greetingTitle: formattedGreetingTitle,
    greetingOptions,
    shouldShowGreetingOptions,
  }
}
